import { MAX_UPLOAD_BYTES, ZuruflexApiError } from '@zuruflex/api-client';
import { formatNumberIn, toLocale } from '@zuruflex/i18n';
import {
  FUEL_LEVELS,
  HANDOVER_PHOTO_KINDS,
  type Booking,
  type BookingHandover,
  type FuelLevel,
  type HandoverKind,
  type HandoverPhotoKind,
  type Vehicle,
} from '@zuruflex/types';
import { Checkbox, cn } from '@zuruflex/ui';
import type { TFunction } from 'i18next';
import { ImagePlus, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api.js';
import { ACCEPTED_IMAGE_TYPES } from './photo-slots.js';

/**
 * The pieces the two handover dialogs share — the pick-up and the return
 * check — so the odometer, the gauge and the four angles look and behave
 * the same at both ends of the hire. That sameness is the point of
 * recording a car twice: the two records are read side by side.
 *
 * The phone has its own copies of all of this in `@zuruflex/mobile-ui`.
 * None of it crosses over — that is React Native, this is the DOM — but the
 * rules do: the same four angles, the same five marks, the same three-step
 * commit, the same catalogue strings under `partner:handover.*`.
 */

/**
 * A booking as `GET /v1/bookings/:reference` actually returns it.
 *
 * The shared `Booking` does not declare the expansions — the same local
 * widening the bookings page carries for the list route. The detail route
 * expands the whole car, which is what the pick-up needs: its item list,
 * its allowance and the odometer it was last seen on.
 */
export interface HandoverBooking extends Booking {
  vehicle?: Vehicle | null;
  customer?: { id: string; fullName: string | null; phone: string | null } | null;
}

/**
 * The four things a partner does before the keys move.
 *
 * The ids the phone files, not labels: `checklist` on the API carries ids,
 * and a record written on the web has to read the same as one written on
 * the forecourt. See `HANDOVER_CHECKS` in the partner app.
 */
export const HANDOVER_CHECKS = ['licence', 'deposit', 'kit', 'rules'] as const;
export type HandoverCheck = (typeof HANDOVER_CHECKS)[number];

/**
 * What ticking "there is new damage" on a return is filed as.
 *
 * The id the phone sends (`DAMAGE_CHECK` in the app's `return.tsx`), so a
 * flag set on the web and one set on a phone are the same flag to whatever
 * eventually acts on it.
 */
export const DAMAGE_CHECK = 'newDamage';

/** Which catalogue string names each angle — the phone's own captions. */
export const HANDOVER_PHOTO_LABEL: Record<HandoverPhotoKind, string> = {
  FRONT_THREE_QUARTER: 'handover.photo.front',
  REAR_THREE_QUARTER: 'handover.photo.rear',
  INTERIOR: 'handover.photo.interior',
  DASHBOARD: 'handover.photo.dashboard',
};

/**
 * The gauge's marks as the catalogue spells them.
 *
 * The API stores the enum and the copy under `handover.fuel.*` is keyed in
 * the phone's camelCase, so the translation happens here, once, rather than
 * at every `t()` that names a level.
 */
export const FUEL_KEY: Record<FuelLevel, string> = {
  EMPTY: 'empty',
  QUARTER: 'quarter',
  HALF: 'half',
  THREE_QUARTERS: 'threeQuarters',
  FULL: 'full',
};

/** A refusal the API sent, in its own words, or the generic sentence. */
export function describeFailure(cause: unknown, fallback: string): string {
  return cause instanceof ZuruflexApiError ? cause.message : fallback;
}

/* ── Photographs ─────────────────────────────────────────── */

/**
 * One angle, as the dialog holds it.
 *
 * `filed` is what makes a retry cheap. A commit that fails on the third
 * upload has already filed two, and pressing the button again must not
 * send those two up a second time; picking a different photograph for an
 * angle clears it again, because the bytes behind it changed.
 */
export interface HandoverPhotoState {
  /** A picked file not yet sent, or null for one already on the record. */
  file: File | null;
  /** An object URL for a picked file, or the signed URL the record carries. */
  url: string;
  filed: boolean;
}

export type HandoverPhotos = Partial<Record<HandoverPhotoKind, HandoverPhotoState>>;

/** The angles already on a record, as filed slots — a resumed handover. */
export function photosFromRecord(record: BookingHandover | undefined): HandoverPhotos {
  const photos: HandoverPhotos = {};
  for (const photo of record?.photos ?? []) {
    photos[photo.kind] = { file: null, url: photo.url, filed: true };
  }
  return photos;
}

/**
 * The four slots, and the object URLs behind the picked ones.
 *
 * Every URL this creates is revoked when its slot is replaced and when the
 * dialog unmounts, because a partner who picks four photographs, changes
 * their mind about two and closes the dialog would otherwise leave six
 * full-size images pinned in memory for the life of the tab.
 */
export function useHandoverPhotos(initial: HandoverPhotos) {
  const [photos, setPhotos] = useState<HandoverPhotos>(initial);
  const created = useRef(new Set<string>());

  useEffect(() => {
    const urls = created.current;
    return () => {
      for (const url of urls) URL.revokeObjectURL(url);
      urls.clear();
    };
  }, []);

  const pick = useCallback((kind: HandoverPhotoKind, file: File) => {
    const url = URL.createObjectURL(file);
    created.current.add(url);
    setPhotos((current) => {
      const previous = current[kind]?.url;
      if (previous && created.current.has(previous)) {
        URL.revokeObjectURL(previous);
        created.current.delete(previous);
      }
      return { ...current, [kind]: { file, url, filed: false } };
    });
  }, []);

  const markFiled = useCallback((kind: HandoverPhotoKind) => {
    setPhotos((current) => {
      const slot = current[kind];
      return slot ? { ...current, [kind]: { ...slot, filed: true } } : current;
    });
  }, []);

  const count = HANDOVER_PHOTO_KINDS.filter((kind) => photos[kind]).length;

  return { photos, pick, markFiled, count, complete: count === HANDOVER_PHOTO_KINDS.length };
}

/** Mirrors ALLOWED_PHOTO_TYPES in the API's handover service. */
const ALLOWED_PHOTO_TYPES = ACCEPTED_IMAGE_TYPES.split(',');

/**
 * Refused here rather than after a presign the API would have refused
 * anyway. Returns the sentence to show, or null for a file that will do.
 */
export function photoProblem(file: File, t: TFunction): string | null {
  if (!ALLOWED_PHOTO_TYPES.includes(file.type)) return t('partner:handover.web.wrongType');
  if (file.size > MAX_UPLOAD_BYTES) {
    return t('partner:verify.tooBig', { max: Math.floor(MAX_UPLOAD_BYTES / 1024 / 1024) });
  }
  return null;
}

/**
 * One photograph, filed against one end of the hire.
 *
 * The same three-step dance as a car's gallery photo — ask for a PUT URL,
 * send the bytes straight to S3, file the key — except that the routes are
 * the booking's, because who may photograph a handover is a question about
 * the booking rather than about the car. `ZuruflexClient` has no
 * one-call helper for this one, so the dance is spelt out here.
 */
export async function uploadHandoverPhoto(
  reference: string,
  file: File,
  { kind, photoKind }: { kind: HandoverKind; photoKind: HandoverPhotoKind },
  failed: string,
): Promise<void> {
  const { uploadUrl, key } = await api.handoverPhotoUploadUrl(reference, {
    kind,
    photoKind,
    contentType: file.type,
  });

  const upload = await fetch(uploadUrl, {
    method: 'PUT',
    // Pinned in the signature, so it has to match exactly.
    headers: { 'Content-Type': file.type },
    body: file,
  });
  if (!upload.ok) {
    throw new ZuruflexApiError(upload.status, { code: 'SERVICE_UNAVAILABLE', message: failed });
  }

  await api.attachHandoverPhoto(reference, { kind, photoKind, key });
}

/**
 * The four angles, in a row on a desktop and two-by-two on anything
 * narrower.
 *
 * Named slots rather than one multi-file input, for the reason the car's
 * gallery has them: "four photos" is not the instruction, "these four" is,
 * and the pick-up's front three-quarter is only worth having if the
 * return has one to put beside it.
 */
export function HandoverPhotoGrid({
  photos,
  onPick,
  busyKind,
  disabled,
  onRejected,
}: {
  photos: HandoverPhotos;
  onPick: (kind: HandoverPhotoKind, file: File) => void;
  /** The angle uploading right now, during a commit. */
  busyKind: HandoverPhotoKind | null;
  disabled: boolean;
  /** A file that will not do, and why. */
  onRejected: (problem: string) => void;
}) {
  const { t } = useTranslation(['partner', 'common']);
  const done = HANDOVER_PHOTO_KINDS.filter((kind) => photos[kind]).length;

  return (
    <section>
      <div className="flex items-baseline justify-between gap-3">
        <SectionLabel>{t('handover.app.record.photos')}</SectionLabel>
        <span className={cn('text-xs', done === 4 ? 'text-success' : 'text-text-muted')}>
          {t('handover.app.record.photosDone', { count: done })}
        </span>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {HANDOVER_PHOTO_KINDS.map((kind) => (
          <HandoverPhotoSlot
            key={kind}
            label={t(HANDOVER_PHOTO_LABEL[kind])}
            previewUrl={photos[kind]?.url}
            busy={busyKind === kind}
            disabled={disabled}
            onPick={(file) => {
              const problem = photoProblem(file, t);
              if (problem) onRejected(problem);
              else onPick(kind, file);
            }}
          />
        ))}
      </div>
      <p className="mt-2 text-xs text-text-muted">
        {t('handover.web.photoHint', { max: Math.floor(MAX_UPLOAD_BYTES / 1024 / 1024) })}
      </p>
    </section>
  );
}

/**
 * One angle. The car gallery's `PhotoSlot`, minus the parts that only make
 * sense for a listing: no remove (an angle is replaced, never emptied — all
 * four are required) and no per-kind hint.
 */
function HandoverPhotoSlot({
  label,
  previewUrl,
  busy,
  disabled,
  onPick,
}: {
  label: string;
  previewUrl?: string;
  busy: boolean;
  disabled: boolean;
  onPick: (file: File) => void;
}) {
  const { t } = useTranslation(['partner', 'common']);
  const input = useRef<HTMLInputElement>(null);

  return (
    <div className="flex flex-col gap-1.5">
      <div
        className={cn(
          'relative aspect-[4/3] overflow-hidden rounded-field border',
          previewUrl ? 'border-border-default' : 'border-dashed border-border-strong',
        )}
      >
        <button
          type="button"
          disabled={disabled || busy}
          aria-label={previewUrl ? `${label} — ${t('common:action.change')}` : label}
          onClick={() => input.current?.click()}
          className="size-full"
        >
          {previewUrl ? (
            <img src={previewUrl} alt="" className="size-full object-cover" />
          ) : (
            <span className="flex size-full flex-col items-center justify-center gap-1 text-text-muted hover:text-text">
              <ImagePlus className="size-5" />
              <span className="text-[11px] font-medium">{t('common:action.add')}</span>
            </span>
          )}
        </button>

        {busy ? (
          <span className="absolute inset-0 flex items-center justify-center bg-overlay">
            <Loader2 className="size-5 animate-spin text-text-inverse" />
          </span>
        ) : null}

        {/*
          The API's own list rather than `image/*`. Safari hands over a
          JPEG when the list leaves HEIC out, and an iPhone photo the
          presign would refuse is otherwise the commonest way this fails.
        */}
        <input
          ref={input}
          type="file"
          accept={ACCEPTED_IMAGE_TYPES}
          hidden
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) onPick(file);
            // Cleared so picking the same file twice still fires.
            event.target.value = '';
          }}
        />
      </div>
      <p className="text-xs font-medium text-text">
        {label}
        <span className="ml-0.5 text-danger">*</span>
      </p>
    </div>
  );
}

/* ── The reading and the gauge ───────────────────────────── */

/**
 * Whole kilometres, copied off a dial.
 *
 * Digits only, and kept as the string that was typed: a field that
 * reformatted itself with thousands separators on every keystroke would
 * move the caret under the partner's fingers. The grouped figure is
 * printed in the hints instead, where it is read rather than edited.
 */
export function OdometerField({
  value,
  onChange,
  hint,
  error,
  disabled,
}: {
  value: string;
  onChange: (digits: string) => void;
  hint?: ReactNode;
  error?: string;
  disabled?: boolean;
}) {
  const { t } = useTranslation(['partner', 'common']);
  const id = useId();
  const hintId = `${id}-hint`;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-text">
        {t('handover.app.record.odometer')}
        <span className="ml-0.5 text-danger">*</span>
      </label>
      <div
        className={cn(
          'flex h-11 items-center gap-2 rounded-field border bg-surface px-3 focus-within:ring-4 focus-within:ring-accent/30',
          error ? 'border-danger' : 'border-border-strong',
        )}
      >
        <input
          id={id}
          inputMode="numeric"
          autoComplete="off"
          maxLength={7}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          aria-describedby={hint || error ? hintId : undefined}
          value={value}
          onChange={(event) => onChange(event.target.value.replace(/\D/g, ''))}
          placeholder="0"
          className="min-w-0 flex-1 bg-transparent font-mono text-lg tabular-nums text-text outline-none placeholder:text-text-subtle"
        />
        <span className="text-sm text-text-muted">{t('handover.km')}</span>
      </div>
      {error ? (
        <p id={hintId} className="text-xs font-medium text-danger">
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="text-xs text-text-muted">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

/** "12 480 km", in the partner's own grouping. */
export function useKm() {
  const { t, i18n } = useTranslation(['partner', 'common']);
  const locale = toLocale(i18n.language);
  return (km: number) => `${formatNumberIn(km, locale)} ${t('handover.km')}`;
}

/**
 * The five marks on the dial, as five buttons.
 *
 * A radio group rather than a select: the marks are the whole vocabulary,
 * and seeing all five at once is what lets a partner point at the one the
 * needle is on with the renter looking over their shoulder.
 */
export function FuelField({
  value,
  onChange,
  hint,
  disabled,
}: {
  value: FuelLevel | null;
  onChange: (level: FuelLevel) => void;
  hint?: ReactNode;
  disabled?: boolean;
}) {
  const { t } = useTranslation(['partner', 'common']);
  const labelId = useId();

  return (
    <div className="flex flex-col gap-1.5">
      <span id={labelId} className="text-sm font-medium text-text">
        {t('handover.app.record.fuel')}
        <span className="ml-0.5 text-danger">*</span>
      </span>
      <div role="radiogroup" aria-labelledby={labelId} className="grid grid-cols-5 gap-1.5">
        {FUEL_LEVELS.map((level) => {
          const selected = value === level;
          return (
            <button
              key={level}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={t(`handover.fuel.level.${FUEL_KEY[level]}`)}
              title={t(`handover.fuel.level.${FUEL_KEY[level]}`)}
              disabled={disabled}
              onClick={() => onChange(level)}
              className={cn(
                'flex h-11 items-center justify-center rounded-field border text-base font-semibold transition-colors',
                'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-accent/30',
                selected
                  ? 'border-accent bg-accent text-accent-text'
                  : 'border-border-default bg-surface text-text hover:bg-surface-hover',
              )}
            >
              {t(`handover.fuel.short.${FUEL_KEY[level]}`)}
            </button>
          );
        })}
      </div>
      <p className="text-xs text-text-muted">
        {value ? t(`handover.fuel.level.${FUEL_KEY[value]}`) : null}
        {value && hint ? ' · ' : null}
        {hint}
      </p>
    </div>
  );
}

/* ── Layout ───────────────────────────────────────────────── */

/** The eyebrow every section of both dialogs opens with. */
export function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <h3 className="text-[11px] font-bold uppercase tracking-widest text-text-muted">{children}</h3>
  );
}

/**
 * One ticked row: a box, a sentence, and an optional line under it.
 *
 * The whole row is the label, so the target is the sentence rather than a
 * twenty-pixel square — this is ticked off with a car key in the other hand.
 */
export function TickRow({
  checked,
  onChange,
  title,
  body,
  aside,
  disabled,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  title: ReactNode;
  body?: ReactNode;
  /** Pinned to the right — a "not back" flag on a return. */
  aside?: ReactNode;
  disabled?: boolean;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3 border-b border-border-default py-3 last:border-b-0">
      <Checkbox
        checked={checked}
        disabled={disabled}
        onCheckedChange={(next) => onChange(next === true)}
        className="mt-0.5"
      />
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium text-text">{title}</span>
        {body ? <span className="mt-0.5 block text-xs text-text-muted">{body}</span> : null}
      </span>
      {aside}
    </label>
  );
}
